import { CommonCMS, Wishlist } from "@lib/constants/contentful";
import type {
    AddProductAndRemoveOldestMutationVariables,
    AddProductToWishlistMutationVariables,
    RemoveProductFromWishlistMutationVariables,
    ChangeQuantityInWishlistMutationVariables,
    ChangeProductSizeInWishlistMutationVariables,
    CreateMyWishlistMutationVariables,
    DeleteMyWishlistMutationVariables,
    ShoppingList,
    ShoppingListLineItem,
} from "@graphql/generated/types";
import type { ToastErrorMessage } from "../store-utility/storeUtility";
import type { MicrocopyGetOptions } from "@lib/contentful/microcopy/types";
import type { GraphQLResponse } from "graphql-request";

type ErrorToasterFunction = (message: ToastErrorMessage) => number | string;
// TODO: Replace with actual datamodel
type MyShoppingListModel = unknown;

type MicrocopyGetFunction = (
    microcopySetKey: string,
    microcopyKey: string,
    options?: MicrocopyGetOptions
) => string;

const storeKey = process.env.NEXT_PUBLIC_STORE_KEY;

export function showErrorToaster(
    getMicrocopy: MicrocopyGetFunction,
    errorToaster: ErrorToasterFunction
): void {
    errorToaster({
        title: getMicrocopy(CommonCMS.shared, CommonCMS.error.general.title),
        description: getMicrocopy(CommonCMS.shared, CommonCMS.error.general.description),
    });
}

export async function createMyShoppingList(
    locale: string,
    getMicrocopy: MicrocopyGetFunction,
    executeCreateAction: (
        variables: CreateMyWishlistMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>,
    errorToaster: ErrorToasterFunction
): Promise<void> {
    const { errors: getCreateMyShoppingListError } = await executeCreateAction({
        storeKey: storeKey,
        locale: locale,
        value: getMicrocopy(CommonCMS.page, Wishlist.title) ?? "My Shopping List",
    });

    if (getCreateMyShoppingListError) {
        showErrorToaster(getMicrocopy, errorToaster);
        throw getCreateMyShoppingListError;
    }
}

export async function deleteMyShoppingList(
    locale: string,
    shoppingList: ShoppingList,
    getMicrocopy: MicrocopyGetFunction,
    executeDeleteAction: (
        variables: DeleteMyWishlistMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>,
    errorToaster: ErrorToasterFunction
): Promise<void> {
    const { errors: getDeleteMyShoppingListError } = await executeDeleteAction({
        shoppingListId: shoppingList.id,
        shoppingListVersion: shoppingList.version,
        storeKey: storeKey,
        locale: locale,
    });

    if (getDeleteMyShoppingListError) {
        showErrorToaster(getMicrocopy, errorToaster);
        throw getDeleteMyShoppingListError;
    }
}

export async function addToMyShoppingList(
    locale: string,
    currentShoppingList: ShoppingList,
    sku: string,
    executeAddAction: (
        variables: AddProductToWishlistMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>,
    executeAddAndRemoveOldestAction: (
        variables: AddProductAndRemoveOldestMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>,
    quantity?: number
): Promise<void> {
    const queryParams = {
        shoppingListId: currentShoppingList.id,
        shoppingListVersion: currentShoppingList.version,
        storeKey: storeKey,
        locale: locale,
    };

    const { errors: getAddToMyShoppingListError } = await (currentShoppingList.lineItems.length >=
    250
        ? executeAddAndRemoveOldestAction({
              oldestItemId: currentShoppingList.lineItems[0].id,
              oldestItemQuantity: currentShoppingList.lineItems[0].quantity,
              sku: sku,
              ...queryParams,
          })
        : executeAddAction({
              ...queryParams,
              sku: sku,
              quantity: quantity ?? 1,
          }));

    if (getAddToMyShoppingListError) {
        throw getAddToMyShoppingListError;
    }
}

export async function removeFromMyShoppingList(
    locale: string,
    shoppingListLineItem: ShoppingListLineItem,
    currentShoppingList: ShoppingList,
    executeRemoveAction: (
        variables: RemoveProductFromWishlistMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>
): Promise<void> {
    const { errors: getRemoveFromMyShoppingListError } = await executeRemoveAction({
        shoppingListId: currentShoppingList.id,
        shoppingListVersion: currentShoppingList.version,
        storeKey: storeKey,
        locale: locale,
        lineItemId: shoppingListLineItem.id,
        quantity: shoppingListLineItem.quantity,
    });

    if (getRemoveFromMyShoppingListError) {
        throw getRemoveFromMyShoppingListError;
    }
}

export async function updateProductQtyInMyShoppingList(
    locale: string,
    shoppingListLineItem: ShoppingListLineItem,
    currentShoppingList: ShoppingList,
    executeUpdateQtyAction: (
        variables: ChangeQuantityInWishlistMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>,
    newQty: number
): Promise<void> {
    const { errors: getUpdateProductQtyError } = await executeUpdateQtyAction({
        lineItemId: shoppingListLineItem.id,
        quantity: newQty,
        shoppingListId: currentShoppingList.id,
        shoppingListVersion: currentShoppingList.version,
        storeKey: currentShoppingList.store.key,
        locale: locale,
    });

    if (getUpdateProductQtyError) {
        throw getUpdateProductQtyError;
    }
}

export async function updateProductSizeInMyShoppingList(
    locale: string,
    shoppingListLineItem: ShoppingListLineItem,
    currentShoppingList: ShoppingList,
    executeUpdateSizeAction: (
        variables: ChangeProductSizeInWishlistMutationVariables
    ) => Promise<GraphQLResponse<MyShoppingListModel>>,
    sku: string
): Promise<void> {
    const { errors: getUpdateProductSizeError } = await executeUpdateSizeAction({
        lineItemId: shoppingListLineItem.id,
        sku: sku,
        shoppingListId: currentShoppingList.id,
        shoppingListVersion: currentShoppingList.version,
        storeKey: currentShoppingList.store.key,
        locale: locale,
        addedAt: shoppingListLineItem.addedAt,
    });

    if (getUpdateProductSizeError) {
        throw getUpdateProductSizeError;
    }
}
